import { useEffect, useCallback } from 'react';
import { useAppDispatch } from './store/hooks';
import { getProfile } from './features/auth/thunks/auth.thunk';
import { logout } from './features/auth/slices/auth.slice';
import { TokenManager } from './utils/storage/tokenmanager.storage';
import { API_CONFIG } from './utils/apiConfig';
import { authRecovery } from './utils/authRecoveryUtils';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { NotificationToastProvider } from './contexts/NotificationToastContext';
import NotificationSocketListener from './components/NotificationSocketListener';
import DeactivatedAccountGuard from './components/DeactivatedAccountGuard';
import { ContactSupportProvider } from './contexts/ContactSupportContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { AuthListener } from './components/AuthListener';
import ContactSupportModal from './components/ContactSupportModal';
import LoginModal from './components/LoginModal';
import PWAInstallPrompt from './components/Pwainstallprompt';
import AppUpdatePrompt from './components/AppUpdatePrompt';
import AssistantWidget from './components/assistant/AssistantWidget';
import ErrorBoundary from './pages/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import ChatLayout from './layouts/ChatLayout';
import NotificationLayout from './layouts/NotificationLayout';
import SettingsLayout from './layouts/SettingsLayout';
import RequireAuth from './components/auth/RequireAuth';
import RequireProfileComplete from './components/auth/RequireProfileComplete';
import GuestRoute from './components/auth/GuestRoute';

// Route Buckets
import { guestAuthRoutes, hybridAuthRoutes } from './routes/auth.routes';
import { dashboardRoutes } from './routes/dashboard.routes';
import { homeRoutes } from './routes/home.routes';
import { chatRoutes } from './routes/chat.routes';
import { listingsRequestsRoutes } from './routes/listings-requests.routes';
import { notificationRoutes } from './routes/notifications.routes';
import { settingsRoutes } from './routes/settings.routes';

// Pages
import Unauthorized from './pages/BaoUnauthorized';

// Trigger token validation immediately when this module loads
if (TokenManager.getAccessToken() && TokenManager.getRefreshToken()) {
  TokenManager.validateAndRefreshIfNeeded().catch(console.error);
}

function AppContent() {
  const dispatch = useAppDispatch();

  // Initialize session: fetch profile after tokens are validated
  useEffect(() => {
    const initializeSession = async () => {
      if (TokenManager.getAccessToken() && TokenManager.getRefreshToken()) {
        try {
          // First, perform health check and recovery if needed
          await authRecovery.performHealthCheck();

          // Validate tokens first
          await TokenManager.validateAndRefreshIfNeeded();
          // Then fetch profile if still authenticated
          if (TokenManager.isAuthenticated()) {
            dispatch(getProfile());
          }
        } catch (error) {
          console.error('Session initialization failed:', error);
          // Try recovery as last resort
          const recovered = await authRecovery.attemptRecovery();
          if (!recovered) {
          }
        }
      }
    };

    initializeSession();
  }, [dispatch]);

  // Cross-tab session sync: detect logout/login and token refresh in other browser tabs
  const handleStorageChange = useCallback(async (e: StorageEvent) => {
    if (e.key === API_CONFIG.TOKEN_STORAGE_KEY) {
      if (!e.newValue) {
        // Token was removed in another tab (logout)
        dispatch(logout());
      } else if (e.newValue !== e.oldValue) {
        // Token was updated in another tab (refresh)
        try {
          await authRecovery.performHealthCheck();
          dispatch(getProfile());
          TokenManager.scheduleTokenRefresh();
        } catch (error) {
          console.error('Cross-tab sync failed:', error);
          const recovered = await authRecovery.attemptRecovery();
          if (!recovered) {
            dispatch(logout());
          }
        }
      }
    }
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [handleStorageChange]);

  return (
    <Routes>
      {/* Auth Routes - Guest Only */}
      <Route element={
        <GuestRoute>
          <AuthLayout />
        </GuestRoute>
      }>
        {guestAuthRoutes}
      </Route>

      {/* Auth Routes - Hybrid/Registration Flow */}
      <Route element={<AuthLayout />}>
        {hybridAuthRoutes}
      </Route>

      {/* Dashboard Routes - Protected & Profile Complete */}
      <Route element={
        <RequireAuth>
          <RequireProfileComplete>
            <DashboardLayout />
          </RequireProfileComplete>
        </RequireAuth>
      }>
        {dashboardRoutes}
      </Route>

      {/* Listings & Requests (Dashboard-like) - Protected & Profile Complete */}
      <Route element={
        <RequireAuth>
          <RequireProfileComplete>
            <DashboardLayout />
          </RequireProfileComplete>
        </RequireAuth>
      }>
        {listingsRequestsRoutes}
      </Route>

      {/* Notifications - Protected & Profile Complete */}
      <Route element={
        <RequireAuth>
          <RequireProfileComplete>
            <NotificationLayout />
          </RequireProfileComplete>
        </RequireAuth>
      }>
        {notificationRoutes}
      </Route>

      {/* Chat - Protected & Profile Complete */}
      <Route element={
        <RequireAuth>
          <RequireProfileComplete>
            <ChatLayout />
          </RequireProfileComplete>
        </RequireAuth>
      }>
        {chatRoutes}
      </Route>

      {/* Settings - Protected & Profile Complete */}
      <Route element={
        <RequireAuth>
          <RequireProfileComplete>
            <SettingsLayout />
          </RequireProfileComplete>
        </RequireAuth>
      }>
        {settingsRoutes}
      </Route>

      {/* Main / Public Routes */}
      <Route element={<MainLayout />}>
        {homeRoutes}
        <Route path="/unauthorized" element={<Unauthorized />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <NotificationToastProvider>
          <ContactSupportProvider>
            <NotificationSocketListener />
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <DeactivatedAccountGuard />
              <AuthListener />
              <AuthModalProvider>
                <AppContent />
                <ContactSupportModal />
                <LoginModal />
                <AssistantWidget />
                <PWAInstallPrompt />
                <AppUpdatePrompt />
              </AuthModalProvider>
            </Router>
          </ContactSupportProvider>
        </NotificationToastProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
